/**
 * Webtech HR Engine™ - Employee Accounts Management
 * Page de gestion des comptes utilisateurs pour les employés
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import {
  ArrowLeft,
  Users,
  UserPlus,
  Mail,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Copy,
  Eye,
  EyeOff,
  RefreshCw,
  Send
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import api from '../lib/api';

export default function EmployeeAccountsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const companyId = user?.companyId;
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [creating, setCreating] = useState(false);
  
  // Dialog states
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [resultDialog, setResultDialog] = useState({ open: false, results: null });
  const [showPasswords, setShowPasswords] = useState({});

  useEffect(() => {
    if (companyId) loadData();
  }, [companyId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, empRes] = await Promise.all([
        api.get(`/employee-accounts/stats?company_id=${companyId}`),
        api.get(`/employee-accounts/employees-without-account?company_id=${companyId}`)
      ]);
      setStats(statsRes.data);
      setEmployees(empRes.data.employees || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedEmployees(employees.filter(e => e.hasEmail).map(e => e.id));
    } else {
      setSelectedEmployees([]);
    }
  };

  const handleSelectEmployee = (employeeId, checked) => {
    if (checked) {
      setSelectedEmployees(prev => [...prev, employeeId]);
    } else {
      setSelectedEmployees(prev => prev.filter(id => id !== employeeId));
    }
  };

  const handleCreateSingle = async (employeeId) => {
    try {
      setCreating(true);
      const response = await api.post(
        `/employee-accounts/create-single/${employeeId}?company_id=${companyId}&send_email=true`
      );
      
      toast.success('Compte créé avec succès');
      
      // Show result
      setResultDialog({
        open: true,
        results: {
          total: 1,
          created: response.data.isExisting ? 0 : 1,
          skipped: response.data.isExisting ? 1 : 0,
          errors: 0,
          results: [{
            employeeName: employees.find(e => e.id === employeeId)?.firstName + ' ' + employees.find(e => e.id === employeeId)?.lastName,
            email: response.data.email,
            password: response.data.password || '(compte existant)',
            success: true,
            error: response.data.isExisting ? 'Compte existant lié' : null
          }]
        }
      });
      
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateBulk = async () => {
    try {
      setCreating(true);
      setConfirmDialog(false);
      
      const response = await api.post(
        `/employee-accounts/create-bulk?company_id=${companyId}&send_emails=true`
      );
      
      toast.success(`${response.data.created} compte(s) créé(s) avec succès`);
      
      // Show results
      setResultDialog({
        open: true,
        results: response.data
      });
      
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papiers');
  };

  const togglePasswordVisibility = (index) => {
    setShowPasswords(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const employeesWithEmail = employees.filter(e => e.hasEmail);
  const employeesWithoutEmail = employees.filter(e => !e.hasEmail);

  const isAdmin = user?.role === 'SuperAdmin' || user?.role === 'Admin';

  return (
    <DashboardLayout title="Comptes employés">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <Button variant="ghost" className="mb-2 -ml-2" onClick={() => navigate('/settings')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour aux paramètres
            </Button>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Users className="h-7 w-7 text-webtech-blue" />
              Gestion des comptes employés
            </h1>
            <p className="text-slate-500 mt-1">Créez des comptes pour permettre aux employés d'accéder à leurs évaluations</p>
          </div>
          
          {isAdmin && employeesWithEmail.length > 0 && (
            <Button onClick={() => setConfirmDialog(true)} disabled={creating}>
              {creating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="h-4 w-4 mr-2" />
              )}
              Créer tous les comptes ({employeesWithEmail.length})
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Users className="h-6 w-6 mx-auto text-slate-400 mb-2" />
                <div className="text-2xl font-bold text-slate-900">{stats.totalEmployees || 0}</div>
                <p className="text-sm text-slate-500">Employés actifs</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <CheckCircle2 className="h-6 w-6 mx-auto text-green-500 mb-2" />
                <div className="text-2xl font-bold text-green-600">{stats.withAccounts || 0}</div>
                <p className="text-sm text-slate-500">Avec compte</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <AlertCircle className="h-6 w-6 mx-auto text-yellow-500 mb-2" />
                <div className="text-2xl font-bold text-yellow-600">{stats.withoutAccounts || 0}</div>
                <p className="text-sm text-slate-500">Sans compte</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Mail className="h-6 w-6 mx-auto text-blue-500 mb-2" />
                <div className="text-2xl font-bold text-blue-600">{stats.withEmail || 0}</div>
                <p className="text-sm text-slate-500">Avec email</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Coverage Progress */}
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">Couverture des comptes</span>
              <span className="text-sm font-bold text-slate-900">{stats.coverage || 0}%</span>
            </div>
            <Progress value={stats.coverage || 0} className="h-2" />
          </CardContent>
        </Card>

        {/* Employees without accounts */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-webtech-blue" />
          </div>
        ) : employees.length === 0 ? (
          <Card className="py-12">
            <CardContent className="text-center">
              <CheckCircle2 className="h-16 w-16 mx-auto text-green-500 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Tous les employés ont un compte !</h3>
              <p className="text-slate-500">Tous vos collaborateurs peuvent accéder à leurs évaluations.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Employees with email */}
            {employeesWithEmail.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Mail className="h-5 w-5 text-green-500" />
                    Employés avec email ({employeesWithEmail.length})
                  </CardTitle>
                  <CardDescription>Ces employés peuvent recevoir leurs identifiants par email</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <Checkbox
                            checked={selectedEmployees.length === employeesWithEmail.length}
                            onCheckedChange={handleSelectAll}
                          />
                        </TableHead>
                        <TableHead>Employé</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Département</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {employeesWithEmail.map(emp => (
                        <TableRow key={emp.id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedEmployees.includes(emp.id)}
                              onCheckedChange={(checked) => handleSelectEmployee(emp.id, checked)}
                            />
                          </TableCell>
                          <TableCell className="font-medium">
                            {emp.firstName} {emp.lastName}
                          </TableCell>
                          <TableCell className="text-slate-500">{emp.email}</TableCell>
                          <TableCell className="text-slate-500">{emp.departmentName || '-'}</TableCell>
                          <TableCell className="text-right">
                            <Button 
                              size="sm" 
                              onClick={() => handleCreateSingle(emp.id)}
                              disabled={creating}
                            >
                              {creating ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <UserPlus className="h-4 w-4 mr-1" />
                                  Créer
                                </>
                              )}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
            
            {/* Employees without email */}
            {employeesWithoutEmail.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-yellow-500" />
                    Employés sans email ({employeesWithoutEmail.length})
                  </CardTitle>
                  <CardDescription>Ces employés doivent avoir une adresse email pour créer un compte</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employé</TableHead>
                        <TableHead>Poste</TableHead>
                        <TableHead>Département</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {employeesWithoutEmail.map(emp => (
                        <TableRow key={emp.id}>
                          <TableCell className="font-medium">
                            {emp.firstName} {emp.lastName}
                          </TableCell>
                          <TableCell className="text-slate-500">{emp.position || '-'}</TableCell>
                          <TableCell className="text-slate-500">{emp.departmentName || '-'}</TableCell>
                          <TableCell className="text-right">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => navigate(`/employees/${emp.id}/edit`)}
                            >
                              Ajouter email
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialog} onOpenChange={setConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Créer les comptes employés</AlertDialogTitle>
            <AlertDialogDescription>
              Vous allez créer des comptes pour <strong>{employeesWithEmail.length} employé(s)</strong>.
              <br /><br />
              Chaque employé recevra un email avec ses identifiants de connexion.
              Les mots de passe seront générés automatiquement.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleCreateBulk}>
              <Send className="h-4 w-4 mr-2" />
              Créer et envoyer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Results Dialog */}
      <Dialog open={resultDialog.open} onOpenChange={(open) => setResultDialog({ ...resultDialog, open })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              Résultats de la création
            </DialogTitle>
            <DialogDescription>
              {resultDialog.results && (
                <span>
                  {resultDialog.results.created} compte(s) créé(s), 
                  {resultDialog.results.skipped} ignoré(s), 
                  {resultDialog.results.errors} erreur(s)
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {resultDialog.results && (
            <div className="space-y-4">
              {/* Success results with passwords */}
              {resultDialog.results.results?.filter(r => r.success && r.password).map((result, idx) => (
                <div key={idx} className="p-4 border rounded-lg bg-green-50 border-green-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-slate-900">{result.employeeName}</span>
                    <Badge className="bg-green-100 text-green-700">Créé</Badge>
                  </div>
                  <div className="grid gap-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Email:</span>
                      <div className="flex items-center gap-2">
                        <code className="bg-white px-2 py-1 rounded">{result.email}</code>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(result.email)}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Mot de passe:</span>
                      <div className="flex items-center gap-2">
                        <code className="bg-white px-2 py-1 rounded">
                          {showPasswords[idx] ? result.password : '••••••••••••'}
                        </code>
                        <Button variant="ghost" size="sm" onClick={() => togglePasswordVisibility(idx)}>
                          {showPasswords[idx] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(result.password)}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Skipped/Existing */}
              {resultDialog.results.results?.filter(r => r.error && r.success).map((result, idx) => (
                <div key={`skip-${idx}`} className="p-4 border rounded-lg bg-blue-50 border-blue-200">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-900">{result.employeeName}</span>
                    <Badge className="bg-blue-100 text-blue-700">{result.error}</Badge>
                  </div>
                </div>
              ))}
              
              {/* Errors */}
              {resultDialog.results.results?.filter(r => !r.success).map((result, idx) => (
                <div key={`err-${idx}`} className="p-4 border rounded-lg bg-red-50 border-red-200">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-900">{result.employeeName}</span>
                    <Badge className="bg-red-100 text-red-700">{result.error || 'Erreur'}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setResultDialog({ open: false, results: null })}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
